import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  History as HistoryIcon,
  Bedtime as SleepIcon,
  Water as HydrationIcon,
  Restaurant as NutritionIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { DailyActivity, User } from '../../types';
import { activityService } from '../../services/activityService';
import { useAuth } from '../../context/AuthContext';

interface AthleteHealthMetricsEditorProps {
  open: boolean;
  onClose: () => void;
  athlete: User;
  teamId: string;
}

type ViewMode = 'list' | 'edit' | 'create';

const AthleteHealthMetricsEditor: React.FC<AthleteHealthMetricsEditorProps> = ({
  open,
  onClose,
  athlete,
  teamId,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [activities, setActivities] = useState<DailyActivity[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<DailyActivity | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Form state
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [sleepScore, setSleepScore] = useState(0);
  const [hydrationLevel, setHydrationLevel] = useState(0);
  const [nutritionLevel, setNutritionLevel] = useState(0);
  const [coachNotes, setCoachNotes] = useState('');

  useEffect(() => {
    if (open) {
      loadHealthMetrics();
      resetForm();
      setViewMode('list');
      setError('');
      setSuccess('');
    }
  }, [open, athlete.id]);

  const loadHealthMetrics = async () => {
    setLoading(true);
    try {
      // Pass teamId to allow head coaches to fetch activities without permission issues
      const history = await activityService.getAthleteHealthMetricsHistory(athlete.id, 30, teamId);
      setActivities(history);
    } catch (err: any) {
      setError('Failed to load health metrics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedDate(new Date());
    setSleepScore(0);
    setHydrationLevel(0);
    setNutritionLevel(0);
    setCoachNotes('');
    setSelectedActivity(null);
  };

  const handleEdit = (activity: DailyActivity) => {
    setSelectedActivity(activity);
    setSelectedDate(parseISO(activity.date));
    setSleepScore(activity.sleepScore);
    setHydrationLevel(activity.hydrationLevel);
    setNutritionLevel(activity.nutritionLevel);
    setCoachNotes(activity.coachNotes || '');
    setViewMode('edit');
    setError('');
    setSuccess('');
  };

  const handleCreate = () => {
    resetForm();
    setViewMode('create');
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!user) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const healthData = {
        sleepScore,
        hydrationLevel,
        nutritionLevel,
      };

      if (viewMode === 'edit' && selectedActivity) {
        // Update existing
        const result = await activityService.updateHealthMetricsByCoach(
          selectedActivity.id,
          user.id,
          healthData,
          coachNotes || undefined
        );

        if (result.success) {
          setSuccess(result.message);
          await loadHealthMetrics();
          setViewMode('list');
        } else {
          setError(result.message);
        }
      } else if (viewMode === 'create' && selectedDate) {
        // Create new
        const dateString = format(selectedDate, 'yyyy-MM-dd');
        const result = await activityService.createHealthMetricsForAthlete(
          athlete.id,
          teamId,
          user.id,
          dateString,
          healthData,
          coachNotes || undefined
        );

        if (result.success) {
          setSuccess(result.message);
          await loadHealthMetrics();
          setViewMode('list');
        } else {
          setError(result.message);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save health metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setViewMode('list');
    resetForm();
    setError('');
    setSuccess('');
  };

  const renderMetricSlider = (
    label: string,
    value: number,
    onChange: (value: number) => void,
    icon: React.ReactNode,
    color: string
  ) => (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight="bold">
          {label}
        </Typography>
        <Chip label={`${value}/10`} size="small" sx={{ ml: 'auto' }} />
      </Box>
      <Slider
        value={value}
        onChange={(_, newValue) => onChange(newValue as number)}
        min={0}
        max={10}
        step={1}
        marks
        valueLabelDisplay="auto"
        sx={{
          '& .MuiSlider-thumb': {
            backgroundColor: color,
          },
          '& .MuiSlider-track': {
            backgroundColor: color,
          },
          '& .MuiSlider-rail': {
            opacity: 0.3,
          },
        }}
      />
    </Box>
  );

  const renderEditForm = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {viewMode === 'edit' ? 'Edit' : 'Add'} Health Metrics for {athlete.displayName}
      </Typography>

      {viewMode === 'create' && (
        <Box sx={{ mb: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              maxDate={new Date()}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
      )}

      {viewMode === 'edit' && selectedActivity && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Editing metrics for {format(parseISO(selectedActivity.date), 'MMMM d, yyyy')}
        </Alert>
      )}

      {renderMetricSlider(
        'Sleep Score',
        sleepScore,
        setSleepScore,
        <SleepIcon sx={{ color: 'primary.main' }} />,
        '#1976d2'
      )}

      {renderMetricSlider(
        'Hydration Level',
        hydrationLevel,
        setHydrationLevel,
        <HydrationIcon sx={{ color: 'info.main' }} />,
        '#0288d1'
      )}

      {renderMetricSlider(
        'Nutrition Level',
        nutritionLevel,
        setNutritionLevel,
        <NutritionIcon sx={{ color: 'success.main' }} />,
        '#2e7d32'
      )}

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Coach Notes (Optional)"
        value={coachNotes}
        onChange={(e) => setCoachNotes(e.target.value)}
        placeholder="Add any notes about these metrics..."
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );

  const renderModificationHistory = (activity: DailyActivity) => {
    if (!activity.modificationHistory || activity.modificationHistory.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          No modification history
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon fontSize="small" />
          Modification History
        </Typography>
        {activity.modificationHistory.map((entry, index) => (
          <Box key={index} sx={{ ml: 2, mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" display="block">
              <strong>Modified by {entry.modifiedByRole}</strong> on{' '}
              {entry.timestamp && format(entry.timestamp.toDate(), 'MMM d, yyyy h:mm a')}
            </Typography>
            {entry.notes && (
              <Typography variant="caption" display="block" color="text.secondary">
                Note: {entry.notes}
              </Typography>
            )}
            {Object.entries(entry.changes).length > 0 && (
              <Typography variant="caption" display="block" color="text.secondary">
                Changes:{' '}
                {Object.entries(entry.changes).map(([key, change]: [string, any]) => 
                  `${key}: ${change.from} → ${change.to}`
                ).join(', ')}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const renderList = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Health Metrics for {athlete.displayName}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          size="small"
        >
          Add Entry
        </Button>
      </Box>

      <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)} sx={{ mb: 2 }}>
        <Tab label="Recent Entries" />
        <Tab label="Summary" />
      </Tabs>

      {selectedTab === 0 && (
        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="center">Sleep</TableCell>
                <TableCell align="center">Hydration</TableCell>
                <TableCell align="center">Nutrition</TableCell>
                <TableCell align="center">Avg</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No health metrics found for this athlete
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                activities.map((activity) => {
                  const avg = ((activity.sleepScore + activity.hydrationLevel + activity.nutritionLevel) / 3).toFixed(1);
                  const isCoachModified = activity.lastModifiedByRole === 'coach';
                  
                  return (
                    <TableRow key={activity.id} hover>
                      <TableCell>{format(parseISO(activity.date), 'MMM d, yyyy')}</TableCell>
                      <TableCell align="center">
                        <Chip label={activity.sleepScore} size="small" color="primary" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={activity.hydrationLevel} size="small" color="info" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={activity.nutritionLevel} size="small" color="success" />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          {avg}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isCoachModified ? (
                          <Tooltip title="Modified by coach">
                            <Chip
                              icon={<PersonIcon />}
                              label="Coach"
                              size="small"
                              color="warning"
                            />
                          </Tooltip>
                        ) : (
                          <Chip label="Athlete" size="small" />
                        )}
                        {activity.coachNotes && (
                          <Tooltip title={activity.coachNotes}>
                            <IconButton size="small">
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(activity)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedTab === 1 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Summary Statistics (Last 30 Days)
          </Typography>
          {activities.length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mt: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Average Sleep
                  </Typography>
                  <Typography variant="h4">
                    {(activities.reduce((sum, a) => sum + a.sleepScore, 0) / activities.length).toFixed(1)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Average Hydration
                  </Typography>
                  <Typography variant="h4">
                    {(activities.reduce((sum, a) => sum + a.hydrationLevel, 0) / activities.length).toFixed(1)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Average Nutrition
                  </Typography>
                  <Typography variant="h4">
                    {(activities.reduce((sum, a) => sum + a.nutritionLevel, 0) / activities.length).toFixed(1)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Total Entries
                  </Typography>
                  <Typography variant="h4">{activities.length}</Typography>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Alert severity="info">No data available for summary</Alert>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Health Metrics Editor
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {loading && viewMode === 'list' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : viewMode === 'list' ? (
          renderList()
        ) : (
          renderEditForm()
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AthleteHealthMetricsEditor;

